import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, ButtonGroup, Card, CardContent, Chip, Grid, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography, makeStyles,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import { hostname } from '../providers/api';
import { useTokenOps } from '../components/TokenContext';
import { formatControlValue } from './commercialFormatters';
import {
  CommercialEmpty, CommercialError, CommercialLoading, CommercialPage, CommercialPageHeader,
} from './CommercialUi';
import { CommercialSectionNavigation } from './CommercialNavigation';
import {
  affectedColumnLabel,
  calculationStatusMeaning,
  formatAffectedValue,
  getCalculationRemediation,
  normalizeAffectedRows,
} from './commercialCalculationAssuranceState';

const icons = { PASS: CheckCircleIcon, WARNING: WarningIcon, FAIL: ErrorIcon };

const useStyles = makeStyles(theme => ({
  summary: { height: '100%', borderTop: '3px solid transparent' },
  pass: { borderTopColor: theme.palette.success.main },
  warning: { borderTopColor: theme.palette.warning.main },
  fail: { borderTopColor: theme.palette.error.main },
  controls: {
    alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1.5),
    justifyContent: 'space-between', marginTop: theme.spacing(3), marginBottom: theme.spacing(1),
  },
  block: { height: '100%' },
  blockHeader: { alignItems: 'flex-start', display: 'flex', gap: theme.spacing(2), justifyContent: 'space-between' },
  formula: { background: theme.palette.action.hover, borderRadius: theme.shape.borderRadius, margin: theme.spacing(2, 0), padding: theme.spacing(1.5) },
  table: { maxWidth: '100%', overflowX: 'auto' },
  meanings: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'minmax(0,1fr)' },
  },
  meaning: { alignItems: 'flex-start', display: 'flex', gap: theme.spacing(1) },
  nextStep: {
    alignItems: 'flex-start',
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    gap: theme.spacing(1.5),
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
    [theme.breakpoints.down('xs')]: { alignItems: 'stretch', flexDirection: 'column' },
  },
  affected: { marginTop: theme.spacing(2) },
  affectedValue: { fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  differencePass: { color: theme.palette.success.dark },
  differenceWarning: { color: theme.palette.warning.dark },
  differenceFail: { color: theme.palette.error.main },
  statusPass: { color: theme.palette.success.dark },
  statusWarning: { color: theme.palette.warning.dark },
  statusFail: { color: theme.palette.error.main },
}));

export default function CommercialCalculationAssurancePage() {
  const classes = useStyles();
  const token = useTokenOps().account.accessToken;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('ATTENTION');

  useEffect(() => {
    fetch(`${hostname}/commercial/calculation-assurance`, { headers: { Authorization: `Bearer ${token}` } })
      .then(response => response.ok ? response.json() : Promise.reject(new Error(`Calculation controls failed (${response.status})`)))
      .then(setData)
      .catch(setError);
  }, [token]);

  const blocks = useMemo(() => {
    if (!data) return [];
    if (filter === 'ATTENTION') return data.blocks.filter(block => block.status !== 'PASS');
    if (filter === 'PASS') return data.blocks.filter(block => block.status === 'PASS');
    return data.blocks;
  }, [data, filter]);

  if (error) return <CommercialPage>
    <CommercialPageHeader title="Calculation checks" description="Verify that commercial metrics are mathematically consistent." />
    <CommercialSectionNavigation area="performance" current="calculations" />
    <CommercialError error={error} />
  </CommercialPage>;
  if (!data) return <CommercialPage>
    <CommercialPageHeader title="Calculation checks" description="Verify that commercial metrics are mathematically consistent." />
    <CommercialSectionNavigation area="performance" current="calculations" />
    <CommercialLoading label="Running calculation checks…" />
  </CommercialPage>;

  const attention = data.summary.warning + data.summary.fail;
  return <CommercialPage>
    <CommercialPageHeader
      title="Calculation checks"
      description={`Automated reconciliations verify that commercial metrics are safe to use for decisions. Reporting period: ${data.period}.`}
    />
    <CommercialSectionNavigation area="performance" current="calculations" />
    <Box className={classes.meanings} role="note" aria-label="Calculation status meanings">
      {['PASS', 'WARNING', 'FAIL'].map(status => {
        const MeaningIcon = icons[status];
        const tone = status === 'PASS' ? 'Pass' : status === 'WARNING' ? 'Warning' : 'Fail';
        return <div className={classes.meaning} key={status}>
          <MeaningIcon className={classes[`status${tone}`]} fontSize="small" />
          <Typography variant="body2">{calculationStatusMeaning[status]}</Typography>
        </div>;
      })}
    </Box>
    <Grid container spacing={2}>
      {[
        ['Passed', data.summary.pass, 'pass'],
        ['Warnings', data.summary.warning, 'warning'],
        ['Failed', data.summary.fail, 'fail'],
      ].map(([label, value, tone]) => <Grid item xs={12} sm={4} key={label}>
        <Card className={`${classes.summary} ${classes[tone]}`}>
          <CardContent><Typography color="textSecondary">{label}</Typography><Typography variant="h4">{value}</Typography></CardContent>
        </Card>
      </Grid>)}
    </Grid>

    <div className={classes.controls}>
      <div>
        <Typography variant="h6">Reconciliation blocks</Typography>
        <Typography variant="body2" color="textSecondary">Start with items that need attention, then review passing controls for traceability.</Typography>
      </div>
      <ButtonGroup size="small" color="primary" aria-label="Filter calculation controls">
        <Button variant={filter === 'ATTENTION' ? 'contained' : 'outlined'} onClick={() => setFilter('ATTENTION')}>Needs attention ({attention})</Button>
        <Button variant={filter === 'PASS' ? 'contained' : 'outlined'} onClick={() => setFilter('PASS')}>Passed ({data.summary.pass})</Button>
        <Button variant={filter === 'ALL' ? 'contained' : 'outlined'} onClick={() => setFilter('ALL')}>All ({data.blocks.length})</Button>
      </ButtonGroup>
    </div>

    {blocks.length === 0 ? <Card><CommercialEmpty title="No controls in this view" description="Choose another filter to review calculation results." /></Card> : <Grid container spacing={2}>
      {blocks.map(block => {
        const Icon = icons[block.status] || WarningIcon;
        const tone = block.status === 'PASS' ? 'Pass' : block.status === 'WARNING' ? 'Warning' : 'Fail';
        const remediation = getCalculationRemediation(block.code, block.status);
        const affected = normalizeAffectedRows(block);
        return <Grid item xs={12} md={6} key={block.code}>
          <Card className={classes.block}>
            <CardContent>
              <div className={classes.blockHeader}>
                <div><Typography variant="h6">{block.label}</Typography><Typography variant="caption" color="textSecondary">Control {block.code}</Typography></div>
                <Chip className={classes[`status${tone}`]} icon={<Icon />} label={block.status === 'PASS' ? 'Passed' : block.status === 'WARNING' ? 'Warning' : 'Failed'} variant="outlined" />
              </div>
              <Box className={classes.formula}><Typography variant="body2"><strong>Formula:</strong> {block.formula}</Typography></Box>
              <TableContainer className={classes.table}>
                <Table size="small" aria-label={`${block.label} reconciliation`}>
                  <TableHead><TableRow><TableCell>Observed</TableCell><TableCell>Expected</TableCell><TableCell>Difference</TableCell></TableRow></TableHead>
                  <TableBody><TableRow>
                    <TableCell>{formatControlValue(block.actual)}</TableCell>
                    <TableCell>{formatControlValue(block.expected)}</TableCell>
                    <TableCell className={classes[`difference${tone}`]}>{formatControlValue(block.difference)}</TableCell>
                  </TableRow></TableBody>
                </Table>
              </TableContainer>
              <Typography variant="body2" style={{ marginTop: 16 }}><strong>How to use it:</strong> {block.usage}</Typography>
              {affected.rows?.length ? <Box className={classes.affected}>
                <Typography variant="subtitle2" gutterBottom>Affected records reported by this check</Typography>
                <TableContainer className={classes.table}>
                  <Table size="small" aria-label={`${block.label} affected records`}>
                    <TableHead><TableRow>{affected.columns.map(column => (
                      <TableCell key={column}>{affectedColumnLabel(column)}</TableCell>
                    ))}</TableRow></TableHead>
                    <TableBody>{affected.rows.map((row, rowIndex) => <TableRow key={row.id || rowIndex}>
                      {affected.columns.map(column => <TableCell className={classes.affectedValue} key={column}>
                        {formatAffectedValue(row[column])}
                      </TableCell>)}
                    </TableRow>)}</TableBody>
                  </Table>
                </TableContainer>
              </Box> : <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                This API check is aggregate; it does not currently identify individual affected records.
              </Typography>}
              <Box className={classes.nextStep}>
                <Typography variant="body2">
                  <strong>Next step:</strong> {remediation.instruction}
                </Typography>
                <Button component="a" href={remediation.href} color="primary" size="small">
                  {remediation.label}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>;
      })}
    </Grid>}
  </CommercialPage>;
}
